import { apiRequest } from './client.js';

/**
 * Feature toggles (M01 §11 Feature Toggles, T005/T005c) — real /v1/features
 * contract. The list returns every catalogue feature enriched with the
 * effective per-tenant state; writes carry an explicit scope + effective window.
 */

export const FEATURE_SCOPES = ['COMPANY', 'GLOBAL'] as const;
export type FeatureScope = (typeof FEATURE_SCOPES)[number];

export const FEATURE_SCOPE_LABELS: Readonly<Record<string, string>> = {
  COMPANY: 'This tenant only',
  GLOBAL: 'Global (all tenants)',
};

/** A catalogue feature + its effective per-tenant state. */
export interface FeatureItem {
  readonly featureCode: string;
  readonly description: string;
  readonly defaultEnabled: boolean;
  readonly enabled: boolean;
  readonly scope: FeatureScope | null;
  readonly effectiveFrom: string | null;
  readonly effectiveTo: string | null;
  readonly hasOverride: boolean;
}

export interface SetFeatureInput {
  readonly featureCode: string;
  readonly scope: FeatureScope;
  readonly enabled: boolean;
  /** ISO 8601 datetime with timezone. */
  readonly effectiveFrom?: string;
  readonly effectiveTo?: string;
}

type Tok = string | null | undefined;

function unwrap(
  r: { items: readonly FeatureItem[] } | readonly FeatureItem[] | null,
): readonly FeatureItem[] {
  if (!r) return [];
  return Array.isArray(r)
    ? r
    : (r as { items: readonly FeatureItem[] }).items ?? [];
}

/** GET /v1/features — catalogue + effective per-tenant state. */
export async function listFeatures(
  token?: Tok,
): Promise<readonly FeatureItem[]> {
  const r = await apiRequest<
    { items: readonly FeatureItem[] } | readonly FeatureItem[]
  >('/features', { token });
  return unwrap(r);
}

/** PUT /v1/features — set a toggle (scope + effective window). */
export function setFeature(
  body: SetFeatureInput,
  token?: Tok,
): Promise<unknown> {
  return apiRequest('/features', { method: 'PUT', token, body });
}
